import { Routes } from '@angular/router';

import { editorPermissionGuard } from '@common/guard/editor-permission.guard';
import { entitySwitchGuard } from '@common/guard/entity-switch.guard';
import { loginGuard } from '@common/guard/login.guard';

/**
 * Every fixed top-level URL segment that a `config.nav` tree must never claim (spec §2.3).
 *
 * This is one-directional by design: every fixed route's first path segment must appear here
 * (pinned by the `app.routes.spec.ts` covering test), but not every entry here has a matching
 * Angular route — `api` and `assets` are server/static prefixes with no client-side route.
 */
export const RESERVED_TOP_LEVEL_SLUGS: ReadonlySet<string> = Object.freeze(
  new Set([
    'modules',
    'editor',
    'renderer',
    'assistant',
    'auth',
    'presentations',
    'dashboards',
    'pages',
    'api',
    'assets',
  ]),
);

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('@home-page/home-page.component').then((m) => m.HomePageComponent),
    canActivate: [loginGuard, entitySwitchGuard],
  },
  {
    path: 'modules',
    loadChildren: () => import('@modules/modules.module').then((m) => m.ModulesModule),
    canActivate: [loginGuard, entitySwitchGuard],
  },
  {
    path: 'renderer',
    loadChildren: () => import('@renderer/renderer.module').then((m) => m.RendererModule),
    canActivate: [loginGuard, entitySwitchGuard],
  },
  {
    path: 'editor',
    loadChildren: () => import('@editor/editor.module').then((m) => m.EditorModule),
    canActivate: [editorPermissionGuard, loginGuard, entitySwitchGuard],
  },
  {
    path: 'assistant',
    loadChildren: () => import('@assistant/assistant.module').then((m) => m.AssistantModule),
    canActivate: [loginGuard, entitySwitchGuard],
  },
  { path: 'auth', loadChildren: () => import('@auth/auth.module').then((m) => m.KonnexflowAuthModule) },
  {
    path: 'presentations',
    loadChildren: () => import('@presentation/presentation.module').then((m) => m.PresentationModule),
    canActivate: [loginGuard, entitySwitchGuard],
  },
  {
    path: 'settings',
    loadChildren: () => import('@settings/settings.module').then((m) => m.SettingsModule),
    canActivate: [loginGuard, entitySwitchGuard],
  },
  {
    path: 'dashboards',
    loadChildren: () => import('@dashboard/dashboard.module').then((m) => m.DashboardModule),
    canActivate: [loginGuard, entitySwitchGuard],
  },
  {
    path: 'pages/:id',
    loadComponent: () => import('@modules/component/page/page.component').then((m) => m.PageComponent),
    canActivate: [loginGuard],
    // No entity switch guard - publicly accessible for embedding
  },
  {
    // AIWB-883 T11 — catch-all: any URL not matched above resolves through the nav tree (root
    // mount). Must stay LAST — Angular tries routes in array order. Same guards as the removed
    // workbooks/apps/assistants reserved-page routes, so `?login=`/`?entity=` handling is
    // unchanged.
    path: '**',
    loadComponent: () => import('@workspace/component/nav-tree-host.component').then((m) => m.NavTreeHostComponent),
    canActivate: [loginGuard, entitySwitchGuard],
    data: { mount: 'root' },
  },
];
